import { MAX_NAV_DEPTH } from "./Config";

export function createDefaultAnalyticsDateRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 13 * 24 * 60 * 60 * 1000);
  const toDateText = (value) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: toDateText(start),
    endDate: toDateText(end)
  };
}

export function createEmptyProductEditorDraft() {
  return {
    sku: "",
    model: "",
    category: "Others",
    title: "",
    subtitle: "",
    brandName: "",
    description: "",
    priceUSD: "",
    priceUnitCode: "piece",
    isBestSelling: false,
    salesCount: "0",
    imageUrl: "",
    detailVideoUrl: "",
    moq: "",
    tradeLeadTime: "",
    shippingPort: "",
    packagingType: "",
    loadingAdvice: "",
    logisticsNotes: "",
    serviceCommitmentsText: "",
    afterSalesPolicy: "",
    detailContentText: "",
    mainImagesText: "",
    detailImagesText: "",
    parametersText: ""
  };
}

function toEditorText(value) {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (!value || typeof value !== "object") {
    return "";
  }

  const preferredLocales = ["zh", "en", "es", "fr", "de", "ja", "ko", "ar", "ru", "pt", "it"];
  for (const locale of preferredLocales) {
    const localized = value[locale];
    if (typeof localized === "string" && localized.trim()) {
      return localized;
    }
  }

  for (const localized of Object.values(value)) {
    if (typeof localized === "string" && localized.trim()) {
      return localized;
    }
  }

  return "";
}

export function toProductEditorDraft(product) {
  return {
    sku: toEditorText(product?.sku),
    model: toEditorText(product?.model),
    category: toEditorText(product?.category) || "Others",
    title: toEditorText(product?.name),
    subtitle: toEditorText(product?.subtitle),
    brandName: toEditorText(product?.brandName),
    description: toEditorText(product?.description),
    priceUSD: toEditorText(product?.priceUSD),
    priceUnitCode: toEditorText(product?.priceUnitCode) || "piece",
    isBestSelling: product?.isBestSelling === true,
    salesCount: toEditorText(product?.salesCount ?? 0),
    imageUrl: toEditorText(product?.imageUrl),
    detailVideoUrl: toEditorText(product?.detailVideoUrl),
    moq: toEditorText(product?.moq),
    tradeLeadTime: toEditorText(product?.tradeLeadTime),
    shippingPort: toEditorText(product?.shippingPort),
    packagingType: toEditorText(product?.packagingType),
    loadingAdvice: toEditorText(product?.loadingAdvice),
    logisticsNotes: toEditorText(product?.logisticsNotes),
    serviceCommitmentsText: Array.isArray(product?.serviceCommitments)
      ? product.serviceCommitments.map((item) => toEditorText(item)).filter(Boolean).join("\n")
      : "",
    afterSalesPolicy: toEditorText(product?.afterSalesPolicy),
    detailContentText: toEditorText(product?.detailContent),
    mainImagesText: Array.isArray(product?.mainImages)
      ? product.mainImages.map((item) => toEditorText(item)).filter(Boolean).join("\n")
      : "",
    detailImagesText: Array.isArray(product?.detailImages)
      ? product.detailImages.map((item) => toEditorText(item)).filter(Boolean).join("\n")
      : "",
    parametersText: Array.isArray(product?.parameters)
      ? product.parameters
        .map((item) => {
          const key = toEditorText(item?.key);
          const value = toEditorText(item?.value);
          if (!key || !value) {
            return null;
          }
          return `${key}:${value}`;
        })
        .filter(Boolean)
        .join("\n")
      : ""
  };
}

export function createDefaultSystemConfigDraft() {
  return {
    // MySQL/Redis are managed outside the admin UI and intentionally omitted
    testing: {
      disableFrontendLoginCaptcha: false
    },
    googleAuth: {
      enabled: false,
      clientId: "",
      allowedHostedDomains: "",
      proxyUrl: "",
      allowInsecureOfflineVerify: false
    },
    site: {
      websiteEnabled: true,
      allowUserRegistration: true,
      minimumLoginRole: "guest"
    },
    emailTemplates: {
      branding: {
        companyName: "",
        accentColor: "#7a5446",
        backgroundColor: "#f6efe8",
        logoUrl: "",
        loginUrl: "",
        supportEmail: "",
        supportLine: "",
        footerNote: ""
      },
      registration: {
        subject: "",
        text: "",
        html: ""
      },
      passwordReset: {
        subject: "",
        text: "",
        html: ""
      },
      loginNotice: {
        enabled: false,
        subject: "",
        text: "",
        html: ""
      }
    }
  };
}

export function createDefaultStoreSettingsDraft() {
  return {
    storeId: "default",
    themeTemplate: "style-1",
    themeLayoutConfig: {
      header: {
        height: 78,
        navAlign: "center",
        navButtonStyle: "pill"
      },
      home: {
        moduleOrder: [
          "intro",
          "quick-nav",
          "categories",
          "best-selling",
          "featured",
          "solutions",
          "scenarios",
          "story",
          "process",
          "inquiry"
        ]
      },
      catalog: {
        cardColumns: 3,
        sidebarMode: "left"
      },
      detail: {
        layoutMode: "split"
      }
    },
    themePageConfigs: {
      home: {
        heroEyebrow: "Global Sourcing Support",
        heroTitle: "Your Reliable Partner for Product Sourcing and Customization",
        heroSubtitle: "We support global buyers with product sourcing, customization, sampling, and order follow-up through clear communication and practical cooperation.",
        primaryCtaText: "View Products",
        secondaryCtaText: "Contact Us",
        heroImage: "/assets/about/1.png"
      },
      catalog: {
        heroTitle: "All Products",
        heroDescription: "Browse product categories, compare options, and contact our team for wholesale quotations.",
        emptyMessage: "No products found in this category.",
        ctaText: "Contact Now"
      },
      detail: {
        inquiryTitle: "Request a Sample or Quote",
        inquiryDescription: "Share your target quantity, branding request, and destination market for a tailored reply.",
        primaryCtaText: "Contact Us",
        secondaryCtaText: "Request Quote",
        trustNote: "Private label and export support available"
      }
    },
    customThemeTemplates: [],
    activeCustomThemeTemplateId: "",
    storefrontTitle: "LOYA SPORT",
    adminTitle: "LOYA Console",
    logoUrl: "/assets/about/logo.png",
    logoAlt: "Loya Sport logo",
    topNoticeText: "Verified Supply | Private Label | Global Shipping",
    contactEmail: "",
    contactPhone: "",
    contactWhatsapp: "",
    seoMetaTitle: "",
    seoDescription: "",
    seoKeywords: "",
    seoCanonicalUrl: "",
    seoRobots: "index,follow",
    seoOgTitle: "",
    seoOgDescription: "",
    seoOgImage: "",
    seoTwitterCard: "summary_large_image",
    seoTwitterSite: ""
  };
}

export function createDefaultEditUserForm() {
  return {
    name: "",
    email: "",
    phone: "",
    role: "customer",
    status: "active",
    jobTitle: ""
  };
}

export function createDefaultPasswordForm() {
  return {
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  };
}

export function createDefaultUserPasswordForm() {
  return {
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  };
}

export function createDefaultReviewDraft() {
  return {
    productId: "",
    reviewerName: "",
    country: "",
    rating: "5",
    variationText: "",
    reviewText: "",
    reviewDate: "",
    helpfulCount: "0",
    verifiedPurchase: true,
    repeatBuyer: false
  };
}

export function createDefaultNavMenuDraft() {
  return [
    {
      key: "products",
      label: "Products",
      path: "/products"
    },
    { key: "customization", label: "Custom Service", path: "/custom-service" },
    { key: "solutions", label: "Solutions", path: "/#quality" },
    { key: "blog", label: "Blog", path: "/blog" },
    { key: "about", label: "About", path: "/about" },
    { key: "contact", label: "Contact Us", path: "/contact" }
  ];
}

export function createDefaultBannerDrafts() {
  return [
    {
      id: "customized",
      title: "Custom your own design",
      annotation: "Custom your own design",
      buttonText: "Start OEM Project",
      buttonLink: "/contact",
      contentX: 12,
      contentY: 52,
      imageUrl: "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=1600&q=80"
    }
  ];
}

export function createDraftProductGroupId() {
  return Math.min(2147483647, Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 1000));
}

export function buildProductGroupDraftPath(groupId, label) {
  const params = new URLSearchParams();
  const normalizedGroupId = Number(groupId || 0);
  if (Number.isFinite(normalizedGroupId) && normalizedGroupId > 0) {
    params.set("categoryId", String(normalizedGroupId));
  }
  if (String(label || "").trim()) {
    params.set("category", String(label || "").trim());
  }
  const query = params.toString();
  return query ? `/products?${query}` : "/products";
}

export function isProductGroupDraftItem(item) {
  return Number(item?.groupId || 0) > 0 || String(item?.key || "").startsWith("products-group-");
}

export function normalizeNavDraftItems(items, depth = 1, parentKey = "menu") {
  if (!Array.isArray(items) || depth > MAX_NAV_DEPTH) {
    return [];
  }

  return items
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const key = String(item.key || `${parentKey}-${index + 1}`).trim() || `${parentKey}-${index + 1}`;
      const label = String(item.label || "");
      const groupId = Number(item.groupId || 0);
      const path = isProductGroupDraftItem(item)
        ? buildProductGroupDraftPath(groupId, item.label)
        : String(item.path || "");
      const normalizedItem = {
        key,
        label,
        path,
        visible: item.visible !== false
      };

      if (Number.isFinite(groupId) && groupId > 0) {
        normalizedItem.groupId = groupId;
      }
      if (Number.isFinite(Number(item.parentGroupId || 0)) && Number(item.parentGroupId || 0) > 0) {
        normalizedItem.parentGroupId = Number(item.parentGroupId || 0);
      }
      if (Number.isFinite(Number(item.customerId || 0)) && Number(item.customerId || 0) > 0) {
        normalizedItem.customerId = Number(item.customerId || 0);
      }
      if (String(item.sourcePlatform || "").trim()) {
        normalizedItem.sourcePlatform = String(item.sourcePlatform || "").trim();
      }
      if (String(item.iconImage || item.icon_image || "").trim()) {
        normalizedItem.iconImage = String(item.iconImage || item.icon_image || "").trim().slice(0, 200);
      }
      if (Number.isFinite(Number(item.sortOrder || 0))) {
        normalizedItem.sortOrder = Number(item.sortOrder || 0);
      }

      if (depth < MAX_NAV_DEPTH) {
        const children = normalizeNavDraftItems(item.children, depth + 1, `${key}-child`);
        if (children.length) {
          normalizedItem.children = children;
        }
      }

      return normalizedItem;
    })
    .filter(Boolean);
}

export function sanitizeNavDraftItems(items, depth = 1, parentKey = "menu") {
  if (!Array.isArray(items) || depth > MAX_NAV_DEPTH) {
    return [];
  }

  return items
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const key = String(item.key || `${parentKey}-${index + 1}`).trim();
      const label = String(item.label || "").trim();
      const groupId = Number(item.groupId || 0);
      const path = isProductGroupDraftItem(item)
        ? buildProductGroupDraftPath(groupId, item.label)
        : String(item.path || "").trim();
      if (!key || !label || !path) {
        return null;
      }

      const normalizedItem = {
        key,
        label,
        path,
        visible: item.visible !== false
      };
      if (Number.isFinite(groupId) && groupId > 0) {
        normalizedItem.groupId = groupId;
      }
      if (Number.isFinite(Number(item.parentGroupId || 0)) && Number(item.parentGroupId || 0) > 0) {
        normalizedItem.parentGroupId = Number(item.parentGroupId || 0);
      }
      if (Number.isFinite(Number(item.customerId || 0)) && Number(item.customerId || 0) > 0) {
        normalizedItem.customerId = Number(item.customerId || 0);
      }
      if (String(item.sourcePlatform || "").trim()) {
        normalizedItem.sourcePlatform = String(item.sourcePlatform || "").trim();
      }
      if (String(item.iconImage || item.icon_image || "").trim()) {
        normalizedItem.iconImage = String(item.iconImage || item.icon_image || "").trim().slice(0, 200);
      }
      if (Number.isFinite(Number(item.sortOrder || 0))) {
        normalizedItem.sortOrder = Number(item.sortOrder || 0);
      }
      if (depth < MAX_NAV_DEPTH) {
        const children = sanitizeNavDraftItems(item.children, depth + 1, `${key}-child`);
        if (children.length) {
          normalizedItem.children = children;
        }
      }

      return normalizedItem;
    })
    .filter(Boolean);
}